import { createWebHistory, createRouter } from "vue-router";
import axios from 'axios';
import routes from './routes';
import appConfig from "../../app.config";
import store from '@/state/store';
import studioService from "@/services/studio.service";
import { setSelectedStudio } from "@/services/studioSelection.service";

// Zorunlu sözleşme kontrolü: zorunlu sözleşme varsa kullanıcıyı Profile/Sözleşmeler'e yönlendirir.
// İsterseniz false yaparak kapatabilirsiniz.
const ENABLE_CONTRACT_CHECK = true;

// Basit cache: her route change'de API'yi dövmeyelim
let _contractCheckCache = {
  studioId: null,
  at: 0,
  hasMandatory: false,
};

const CONTRACT_CACHE_TTL_MS = 15000;

// Stüdyosu olmayanların girebileceği İZİNLİ SAYFALAR (Whitelist)
const ALLOWED_NO_STUDIO_ROUTES = [
  '/welcome',
  '/create-studio',
  '/join-studio',
  '/profile',
  '/logout',
];

function isAuthRequired(routeTo) {
  return routeTo.matched.some((route) => route.meta.authRequired);
}

function redirectToLogin(next, routeTo) {
  return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
}

function setAxiosAuthHeader(token) {
  axios.defaults.headers.common['authorization'] = 'Bearer ' + token;
}

function isProfileContractsRoute(routeTo) {
  // name kontrolü legacy olabilir; path kontrolü esas.
  return routeTo.path === '/profile' || routeTo.name === 'Profile';
}

function isLogoutRoute(routeTo) {
  return routeTo.path === '/logout';
}

function ensureSelectedStudioId(studios) {
  // localStorage'da seçili stüdyo yoksa ilkini seçip kaydet
  if (!localStorage.getItem('selectedStudioId') && Array.isArray(studios) && studios.length > 0) {
    setSelectedStudio(studios[0]);
  }
}

async function fetchUserStudios() {
  const resStudios = await studioService.getUserStudios();
  return resStudios?.data?.data || [];
}

async function getHasMandatoryContracts(studioId) {
  const now = Date.now();
  const cacheOk =
    _contractCheckCache.studioId === studioId &&
    (now - _contractCheckCache.at) < CONTRACT_CACHE_TTL_MS;

  if (cacheOk) return _contractCheckCache.hasMandatory;

  const noRedirect = { skipAuthRedirect: true };
  const resContracts = await studioService.getUserUnapprovedContracts({ studioId, _t: now }, noRedirect);
  const contracts = resContracts.data?.data || [];
  const hasMandatory = contracts.some((c) => c.isMandatory === true);
  _contractCheckCache = { studioId, at: now, hasMandatory };
  return hasMandatory;
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {

  const authRequired = isAuthRequired(routeTo);
  
  // Auth gerektirmeyen sayfa ise direkt geç
  if (!authRequired) return next();
  
  const token = localStorage.getItem('jwt');
  
  // Token yoksa Login sayfasına at
  if (!token) return redirectToLogin(next, routeTo);

  // Token varsa header'a ekle
  setAxiosAuthHeader(token);

  // -----------------------------------------------------------------------
  // ✅ STÜDYO + SÖZLEŞME KONTROLÜ
  // -----------------------------------------------------------------------
  try {
    const studios = await fetchUserStudios();
    const hasStudio = studios.length > 0;

    if (!hasStudio) {
      // Stüdyosu yok: whitelist kontrolü
      if (ALLOWED_NO_STUDIO_ROUTES.includes(routeTo.path)) return next();
      return next('/welcome');
    }

    // Stüdyosu var: seçili stüdyo yoksa ayarla
    ensureSelectedStudioId(studios);

    // Zorunlu sözleşme kontrolü (Admin vs önemli değil; kullanıcı bazlı)
    if (ENABLE_CONTRACT_CHECK) {
      const isProfileContracts = isProfileContractsRoute(routeTo);
      const isLogout = isLogoutRoute(routeTo);

      if (!isProfileContracts && !isLogout) {
        const studioId = localStorage.getItem('selectedStudioId');
        if (studioId) {
          const hasMandatory = await getHasMandatoryContracts(studioId);
          if (hasMandatory) {
            return next({ path: '/profile', query: { tab: 'contracts' } });
          }
        }
      }
    }

    return next();
  } catch (error) {
    // 401 = token yok/geçersiz/süresi dolmuş → kullanıcıyı login'e yönlendir.
    if (error?.response?.status === 401 && !error?.config?.skipAuthRedirect) {
      try {
        localStorage.removeItem("jwt");
        localStorage.removeItem("user");
      } catch (e) {
        // ignore
      }
      return redirectToLogin(next, routeTo);
    }

    console.error("Router Kontrol Hatası (stüdyo vb.):", error);
    return next();
  }
});

router.beforeEach((routeTo, routeFrom, next) => {
  const useFirebaseAuth = process.env.VUE_APP_DEFAULT_AUTH === "firebase";
  const authRequired = isAuthRequired(routeTo);

  if (useFirebaseAuth) {
    if (!authRequired) return next();

    if (store.getters['auth/loggedIn']) {
      return store.dispatch('auth/validate').then((validUser) => {
        validUser ? next() : redirectToLogin(next, routeTo);
      });
    }

    return redirectToLogin(next, routeTo);
  }

  const publicPages = ['/login', '/register', '/forgot-password'];
  const authpage = !publicPages.includes(routeTo.path);
  const loggeduser = localStorage.getItem('user');

  if (authpage && !loggeduser) {
    return next('/login');
  }

  return next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;